/**
 * A thin vertical drag handle that resizes the column to its LEFT in the
 * mailbox's desktop 3-pane layout. Pointer-based (works for mouse + touch),
 * with pointer capture so a fast drag doesn't lose the handle. The strip is
 * mostly transparent and highlights on hover/drag so it stays out of the way
 * until reached for. Also keyboard-resizable (←/→) for accessibility.
 *
 * Purely presentational: the page owns the width state; this reports the new
 * width via `onResize`, clamped by the caller's bounds.
 */

import { useRef } from 'react';

export interface ColumnResizerProps {
  /** Current width of the column to the left, px — the drag/keys start from here. */
  width: number;
  min: number;
  max: number;
  /** Called with the proposed new width as the pointer/keys move (caller clamps too). */
  onResize: (nextWidth: number) => void;
  ariaLabel: string;
}

const KEYBOARD_STEP = 16;

export function ColumnResizer({ width, min, max, onResize, ariaLabel }: ColumnResizerProps) {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(width)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      // A comfortable transparent grab lane that sits in its OWN column (no
      // negative margin, no z-index stacking over the list) so the hover
      // highlight can't bleed onto the adjacent rows' content. The visible
      // highlight is a thin bar CENTERED in the lane, shown only on
      // hover/focus/drag — at rest the columns' own borders draw the divider.
      className="group relative flex w-1.5 shrink-0 cursor-col-resize items-stretch justify-center bg-transparent focus-visible:outline-none"
      onPointerDown={(e) => {
        // Ignore secondary buttons; capture so the drag survives leaving the strip.
        if (e.button !== 0) return;
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        drag.current = { startX: e.clientX, startWidth: width };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        onResize(clamp(d.startWidth + (e.clientX - d.startX)));
      }}
      onPointerUp={(e) => {
        drag.current = null;
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          // capture may already be released
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onResize(clamp(width - KEYBOARD_STEP));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          onResize(clamp(width + KEYBOARD_STEP));
        }
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none w-0.5 rounded bg-transparent transition-colors group-hover:bg-primary/50 group-focus-visible:bg-primary/60 group-active:bg-primary/70"
      />
    </div>
  );
}
